import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
  split,
} from "@apollo/client/core";
import { ErrorLink } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { useToast } from "@app/core";
import { createClient } from "graphql-ws";

import { tokenManager } from "./tokenManager";

import router from "@/app/router";
import { useUserStore } from "@/shared/stores/useUserStore";
import { readableGraphQLErrorMessage } from "@/shared/utils/apolloErrors";

// In development, use relative paths so Vite proxy handles CORS and cookies.
// In production, use the full backend URL from VITE_API_URL.
const BASE_URL = import.meta.env.VITE_API_URL_FOR_LOCAL_DEVELOPMENT
  ? ""
  : (import.meta.env.VITE_API_URL ?? "");

const GQL_URL = `${BASE_URL}/graphql`;

const { error: showError } = useToast();

// Lazy ref so refreshAccessToken can call clearStore() even though
// apolloClient is defined later in this file.
let _apolloClient: ApolloClient<unknown> | null = null;

function clearAuthState(): void {
  tokenManager.clearTokens();
  useUserStore().clearUser();
  _apolloClient?.clearStore().catch(() => {
  });
  if (router.currentRoute.value.path !== "/auth") {
    router.push("/auth");
  }
}

// ── Refresh Token (GQL mutation via raw fetch to avoid circular dependency) ──

interface RefreshResponse {
  data?: { refresh?: { accessToken: string } }
  errors?: { message: string }[]
}

let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  // Deduplicate concurrent refresh calls — all waiters get the same promise
  if (refreshPromise) return refreshPromise;

  refreshPromise = fetch(GQL_URL, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: "mutation Refresh { refresh { accessToken } }",
    }),
  })
    .then(res => res.json() as Promise<RefreshResponse>)
    .then((json) => {
      if (json.errors?.length) throw new Error(json.errors[0].message);
      const accessToken = json.data?.refresh?.accessToken;
      if (!accessToken) throw new Error("No access token in refresh response");
      tokenManager.setAccessToken(accessToken);
      return accessToken;
    })
    .catch((err) => {
      clearAuthState();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// ── Auth Link — attaches Bearer token to every request ──────────────────────

const authLink = new ApolloLink((operation, forward) => {
  const token = tokenManager.getAccessToken();

  operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }));

  return forward(operation);
});

// ── Error Link — handles UNAUTHENTICATED: refresh + retry; toasts all others ─

const errorLink = new ErrorLink(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === "UNAUTHENTICATED") {
        // No access token means this is a genuine auth failure (wrong credentials),
        // not an expired session — let the error propagate as-is.
        if (!tokenManager.getAccessToken()) return;

        return new Observable((observer) => {
          refreshAccessToken()
            .then((newToken) => {
              const oldHeaders = operation.getContext().headers as Record<string, string>;
              operation.setContext({
                headers: { ...oldHeaders, Authorization: `Bearer ${newToken}` },
              });
            })
            .then(() => {
              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            })
            .catch(err => observer.error(err));
        });
      }

      showError(readableGraphQLErrorMessage(err));
    }
  }

  if (networkError) {
    const name = operation.operationName;

    if ("statusCode" in networkError) {
      if (networkError.statusCode === 403) {
        showError("Request blocked — access denied", name);
      }
      else {
        showError(networkError.message, name);
      }
    }
    else {
      showError("Network connection failed", name);
    }
  }
});

// TODO: uncomment once backend sets Access-Control-Allow-Credentials: true,
//       Access-Control-Allow-Origin: <exact frontend domain>, and
//       Set-Cookie: SameSite=None; Secure
//
const AUTH_OPERATIONS = new Set(["Login", "Refresh"]);
const httpLink = split(
  ({ operationName }) => AUTH_OPERATIONS.has(operationName),
  new HttpLink({ uri: GQL_URL, credentials: "include" }),
  new HttpLink({ uri: GQL_URL, credentials: "omit" }),
);

// ── WebSocket Link — used for GraphQL subscriptions ──────────────────────────

const WS_BASE = import.meta.env.VITE_API_URL_FOR_LOCAL_DEVELOPMENT
  ? window.location.origin
  : (import.meta.env.VITE_API_URL ?? window.location.origin);

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${WS_BASE.replace(/^http/, "ws")}/graphql`,
    connectionParams: () => ({
      Authorization: `Bearer ${tokenManager.getAccessToken()}`,
    }),
  }),
);

// ── Apollo Client ─────────────────────────────────────────────────────────────

export const apolloClient = new ApolloClient({
  link: split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === "OperationDefinition" && def.operation === "subscription";
    },
    wsLink,
    ApolloLink.from([authLink, errorLink, httpLink]),
  ),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: { fetchPolicy: "network-only" },
    watchQuery: { fetchPolicy: "cache-and-network" },
  },
  devtools: { enabled: import.meta.env.DEV },
});

// Wire the lazy ref so clearAuthState() can call apolloClient.clearStore()
_apolloClient = apolloClient;
